// Lossless pixel-buffer reorientation: 90° CW/CCW rotation, 180° rotation,
// and horizontal/vertical flip. Pure index-permutation — no resampling, so
// round-tripping (e.g. flip-h twice, or rotate-90-cw four times) reproduces
// the source buffer byte-for-byte.
//
// Extracted from RotateImageCommand's whole-canvas `rotateLayer` (audit i24)
// so the same math backs both:
//   - RotateImageCommand (Image > Image Rotation, whole document + all layers)
//   - TransformLayerOrientationCommand (Edit > Transform, single/selected
//     layers only)
// without duplicating the buffer-index arithmetic in two places.

export type OrientationOp = "90-cw" | "90-ccw" | "180" | "flip-h" | "flip-v";

export interface OrientedBuffer {
  readonly data: Uint8ClampedArray;
  readonly width: number;
  readonly height: number;
}

export function rotateOrientationBuffer(
  data: Uint8ClampedArray,
  w: number,
  h: number,
  mode: OrientationOp,
): OrientedBuffer {
  let newW: number, newH: number;
  let rotated: Uint8ClampedArray;

  if (mode === "90-cw") {
    newW = h; newH = w;
    rotated = new Uint8ClampedArray(newW * newH * 4);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const si = (y * w + x) * 4;
        // Bug fix (found while writing TransformLayerOrientationCommand's
        // tests, audit i24): this used to read `(newH - 1 - y)`, but newH
        // is the ORIGINAL width (w), not height. For a non-square buffer
        // that put the destination column outside [0, newW-1], silently
        // scrambling pixels into the wrong row. The correct destination
        // column is derived from the original height `h`.
        const di = (x * newW + (h - 1 - y)) * 4;
        rotated[di] = data[si]!;
        rotated[di + 1] = data[si + 1]!;
        rotated[di + 2] = data[si + 2]!;
        rotated[di + 3] = data[si + 3]!;
      }
    }
  } else if (mode === "90-ccw") {
    newW = h; newH = w;
    rotated = new Uint8ClampedArray(newW * newH * 4);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const si = (y * w + x) * 4;
        const di = ((newH - 1 - x) * newW + y) * 4;
        rotated[di] = data[si]!;
        rotated[di + 1] = data[si + 1]!;
        rotated[di + 2] = data[si + 2]!;
        rotated[di + 3] = data[si + 3]!;
      }
    }
  } else if (mode === "180") {
    newW = w; newH = h;
    rotated = new Uint8ClampedArray(newW * newH * 4);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const si = (y * w + x) * 4;
        const di = ((newH - 1 - y) * newW + (newW - 1 - x)) * 4;
        rotated[di] = data[si]!;
        rotated[di + 1] = data[si + 1]!;
        rotated[di + 2] = data[si + 2]!;
        rotated[di + 3] = data[si + 3]!;
      }
    }
  } else if (mode === "flip-h") {
    newW = w; newH = h;
    rotated = new Uint8ClampedArray(newW * newH * 4);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const si = (y * w + x) * 4;
        const di = (y * newW + (newW - 1 - x)) * 4;
        rotated[di] = data[si]!;
        rotated[di + 1] = data[si + 1]!;
        rotated[di + 2] = data[si + 2]!;
        rotated[di + 3] = data[si + 3]!;
      }
    }
  } else {
    // flip-v
    newW = w; newH = h;
    rotated = new Uint8ClampedArray(newW * newH * 4);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const si = (y * w + x) * 4;
        const di = ((newH - 1 - y) * newW + x) * 4;
        rotated[di] = data[si]!;
        rotated[di + 1] = data[si + 1]!;
        rotated[di + 2] = data[si + 2]!;
        rotated[di + 3] = data[si + 3]!;
      }
    }
  }

  return { data: rotated, width: newW, height: newH };
}

/** The discrete op that undoes `op` exactly (all these ops are involutions or exact inverses). */
export function invertOrientationOp(op: OrientationOp): OrientationOp {
  switch (op) {
    case "90-cw": return "90-ccw";
    case "90-ccw": return "90-cw";
    case "180": return "180";
    case "flip-h": return "flip-h";
    case "flip-v": return "flip-v";
  }
}
